import { createReadingConditionStore } from '@/stores/conditionStore'
import { useMissionStore } from '@/stores/missionStore'
import { useStoreManager } from '@/stores/storeManager'
import { COUNTER, READING, VALUE } from '@/constants'
import { StatisticIndicatorCalculator } from '@/utils/statisticIndicatorCalculator/StatisticIndicatorCalculator'
import { extractLastReadingByDateAndMission } from '@/stores/manager/readingHistoryManager'
import Timer from '@/utils/timer'
import _ from 'lodash'
import questionsHandler from '@/utils/questionsHandler'

export const saveReadingCountersDebounced = _.debounce(saveReadingCounters, 3000)
export function saveReadingCounters(readingId, configuration) {
  const counterTimer = new Timer("saveReadingCounters");
  const readings = useStoreManager().getFromStoreByCategory('reading', 'reading');
  counterTimer.echoStep("get readings");
  const reading = readings[readingId];
  if (reading.status !== READING.STATUS.NEW) {
    return;
  }
  const readingsHistory = useStoreManager().getFromStoreByCategory('readingHistory', 'reading') || {}
  counterTimer.echoStep("get readingsHistory");
  const missionPlannings = useStoreManager().getFromStoreByCategory('mission', 'missionsPlannings');
  counterTimer.echoStep("get missionplannings");
  const pointOfSale = getPosFromMissionPlanningId(reading?.mission_planning, missionPlannings)
  counterTimer.echoStep("get pointOfSale");
  const lastReadingHistory = extractLastReadingByDateAndMission(
    readingsHistory,
    missionPlannings[reading?.mission_planning]?.mission?.id,
  ) || {}
  counterTimer.echoStep("get lastReadingHistory");
  const lastReadingHistoryCounters = keepOnly3NestedCounters(JSON.parse(JSON.stringify(lastReadingHistory?.counters || {})));
  counterTimer.echoStep("get lastReadingHistoryCounters");
  const readingAnswers = useStoreManager().getFromStoreByCategory('reading', 'answer');
  counterTimer.echoStep("get readingAnswers");
  const answers = Object.values(readingAnswers).filter((ra) => ra.reading.toString() === readingId.toString())
  counterTimer.echoStep("get answers");
  const requiredPictures = configuration?.settings?.nb_required_picture || 0;
  counterTimer.echoStep("get requiredPictures");
  const questionsConditionsWithAnswers = getQuestionsConditionsWithAnswers(readingId, answers);
  counterTimer.echoStep("get questionsConditionsWithAnswers");
  const totalCount = questionsConditionsWithAnswers.length;
  counterTimer.echoStep("get totalCount");
  const totalAnswersCount = questionsConditionsWithAnswers.filter(conditionItem => conditionItem.hasAnswer).length;
  counterTimer.echoStep("get totalAnswersCount");
  const totalMandatoryCount = questionsConditionsWithAnswers.filter(conditionItem => conditionItem.mandatory).length;
  counterTimer.echoStep("get totalMandatoryCount");
  const statisticIndicators = new StatisticIndicatorCalculator().calculate( Object.entries(configuration?.statisticIndicators || {}),{
    questionsConditionsWithAnswers: questionsConditionsWithAnswers,
    answers: answers,
    statisticIndicatorsProductIds: pointOfSale?.statisticIndicators
  });
  counterTimer.echoStep("get statisticIndicators");
  const totalMandatoryAnswersCount = questionsConditionsWithAnswers.filter(conditionItem => conditionItem.mandatory && conditionItem.hasAnswer).length;
  counterTimer.echoStep("get totalMandatoryAnswersCount");
  const currentPictures = Object.entries(useStoreManager().getFromStoreByCategory('reading', 'picture'))
    .filter(([key, value]) => readingId && !value.to_remove && value.reading?.toString() === readingId?.toString())
    .map(([key, value]) => {
      return { id: key, ...value }
    })|| {};
  counterTimer.echoStep("get currentPictures");
  reading.counters = {
    total: totalCount,
    answered: totalAnswersCount,
    mandatory: totalMandatoryCount,
    mandatoryAnswered: totalMandatoryAnswersCount,
    optional: totalCount - totalMandatoryCount,
    optionalAnswered: totalAnswersCount - totalMandatoryAnswersCount,
    requiredPictures: requiredPictures,
    pictures: currentPictures.length,
    countStatus: calculateCountStatus(totalAnswersCount, totalMandatoryAnswersCount, totalMandatoryCount),
    statisticIndicators: statisticIndicators,
    lastReadingHistoryCounters: lastReadingHistoryCounters,
    presentDNProductIds: getPresentProductIdsInDNProductIndicatorsProductIds(pointOfSale, questionsConditionsWithAnswers, configuration),
    outOfStockProductIds: getOutOfStockProductIds(questionsConditionsWithAnswers, configuration)
  }
  useStoreManager().update('reading', 'reading', readingId, reading);
  counterTimer.echoStep("update reading");
  counterTimer.echo();
}

function getPosFromMissionPlanningId(missionPlanningId, missionPlannings) {
  const missions = useStoreManager().getFromStoreByCategory('mission', 'missions');
  const pointsOfSale = useStoreManager().getFromStoreByCategory('mission', 'pointsOfSale');
  const missionId = missionPlannings[missionPlanningId]?.mission?.id;
  let posId = null;
  let pos = null;
  if (missionId) {
    posId = missions[missionId]?.pos?.id;
  }
  if (posId) {
    pos = pointsOfSale[posId];
  }
  return pos ? { id: posId, ...pos } : null;
}
function getQuestionsConditions(readingId) {
  return createReadingConditionStore(readingId || VALUE.DEFAULT)?.questions;
}

function buildQuestionsWithAnswer(answers) {
  return answers.reduce((acc, answer) => {
    let code = answer.question.toString()
    if (answer.attachment_id) {
      code += `:${answer.attachment_type}|${answer.attachment_id}`
    }
    acc[code] = answer.value
    return acc
  }, {})
}

export function getQuestionsConditionsWithAnswers(readingId, answers, questionsConditions= null ) {
  if (!questionsConditions) {
    questionsConditions = getQuestionsConditions(readingId) || [];
  }

  const trueAnswersSet = new Set(
    answers
      .filter((answer) => ["true", "1"].includes(answer.value?.toString().toLowerCase()))
      .map((answer) => {
        let code = answer.question.toString();
        if (answer.attachment_id) {
          code += `:${answer.attachment_type}|${answer.attachment_id}`;
        }
        return code;
      })
  );

  const questionsWithAnswers = buildQuestionsWithAnswer(answers);

  return Object.entries(questionsConditions)
    .filter(([_, conditions]) => conditions?.visibility)
    .map(([questionCode, conditions]) => ({
      question_id: questionCode.split(":")[0],
      code: questionCode,
      hasAnswer: !!questionsWithAnswers[questionCode],
      hasTrueAnswer: trueAnswersSet.has(questionCode),
      ...conditions,
    }));
}

function calculateCountStatus(totalAnswersCount, totalMandatoryAnswersCount, totalMandatoryCount) {
  if (totalAnswersCount === 0) {
    return COUNTER.STATUS.NEW;
  }
  else if (totalMandatoryAnswersCount < totalMandatoryCount) {
    return COUNTER.STATUS.NOT_READY;
  }
  else {
    return COUNTER.STATUS.READY;
  }
}

function keepOnly3NestedCounters(lastReadingHistoryCounters) {
  if (lastReadingHistoryCounters?.lastReadingHistoryCounters?.lastReadingHistoryCounters) {
    lastReadingHistoryCounters.lastReadingHistoryCounters.lastReadingHistoryCounters = {}
  }
  return lastReadingHistoryCounters;
}

function getPresentProductIdsInDNProductIndicatorsProductIds(pointOfSale, questionsConditionsWithAnswers, configuration) {
  let output = [];
  const dnProductIndicatorsProductIds = useMissionStore()?.pointsOfSale?.[pointOfSale?.id]?.productIndicators?.dn || []
  const statPresenceDNQuestions = Object.values(configuration?.questions)?.filter(
    question => question?.isPresenceType
  )?.map(question => question?.id?.toString())
  for (const questionsCondition of questionsConditionsWithAnswers) {
    if (!statPresenceDNQuestions.includes(questionsCondition.question_id) || !questionsCondition.hasTrueAnswer) {
      continue;
    }
    for (const productId of dnProductIndicatorsProductIds) {
      if (questionsCondition.code === `${questionsCondition.question_id}:${READING.ATTACHMENT_TYPE.PRODUCT}|${productId}`)
        output.push(productId)
    }
  }
  return output;
}

function getOutOfStockProductIds(questionsConditionsWithAnswers, configuration) {
  let output = [];
  const statOutOfStockTypeQuestions = Object.values(configuration?.questions)?.filter(
    question => question?.isOutOfStockType
  )?.map(question => question?.id?.toString())
  for (const questionsCondition of questionsConditionsWithAnswers) {
    if (!statOutOfStockTypeQuestions.includes(questionsCondition.question_id) || !questionsCondition.hasTrueAnswer) {
      continue;
    }
    const productId = questionsCondition.code.split("|")[1]
    output.push(productId)
  }
  return output;
}

export async function clearInvalidAnswers(readingId) {
  const store = useStoreManager();
  let answersHaveBeenDeleted = false;
  const readingAnswers = store.getFromStoreByCategory('reading', 'answer');
  const relevantAnswers = Object.entries(readingAnswers).filter(
    ([, ra]) => ra.reading?.toString() === readingId.toString()
  );

  const questionsWithValidity = getQuestionsConditionsWithAnswers(
    readingId,
    relevantAnswers.map(([, value]) => value)
  );

  const validityMap = new Map();
  for (const question of questionsWithValidity) {
    validityMap.set(question.code, question);
  }

  const keysToDelete = [];
  for (const [answerKey, answer] of relevantAnswers) {
    const code = answer.attachment_id
      ? questionsHandler.buildId(answer.question, answer.attachment_type, answer.attachment_id)
      : answer.question.toString();

    const validity = validityMap.get(code);
    if (validity?.hasAnswer && !validity.validity) {
      keysToDelete.push(answerKey);
    }
  }

  if (keysToDelete.length > 0) {
    await Promise.all(
      keysToDelete.map((key) => store.hardDelete('reading', 'answer', key))
    );
    answersHaveBeenDeleted = true;
  }

  return answersHaveBeenDeleted;
}